import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { useARC } from "./ARC/ARCProvider";
import { useFeatures } from "./FeaturesProvider";

import { ConnectionState } from "lib/consts/ARC/connection";
import { DefaultEnableGimbalControl, gimbalStatesInfo } from "lib/consts/gimbal";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import {
    setGimbalSpeed,
    setGimbalAngle,
    setGimbalZoom,
    setGimbalAbsoluteZoom,
    setGimbalFocus,
    setGimbalTouchFocus,
    gimbalCenter,
    gimbalAutoFocus,
    gimbalCaptureImage,
    gimbalGetStatus,
} from "lib/protocols/gimbal";

export const GimbalControlContext = createContext();
GimbalControlContext.displayName = "GimbalControl";
export const useGimbalControl = () => useContext(GimbalControlContext);

const GimbalControlProvider = ({ children }) => {
    const { controlSocket, connectionState, sendCommand: send, callService } = useARC();
    const { hasGimbalControl } = useFeatures();

    const [states, setStates] = useState(
        Object.entries(gimbalStatesInfo).reduce(
            (result, [key, value]) => ((result[key] = value.default), result),
            {}
        )
    );
    const [enableGimbalControl, setEnableGimbalControl] = useState(DefaultEnableGimbalControl);
    const [showGimbalControl, setShowGimbalControl] = useState(DefaultEnableGimbalControl);
    const [controlGimbal, setControlGimbal] = useState(false);
    // True only while the Take Photo dialog is open; used by ControlPage to float the
    // joystick above that dialog. Kept separate from controlGimbal (which also toggles for
    // ordinary manual gimbal driving) so the z-index bump is scoped to photo capture.
    const [photoCaptureActive, setPhotoCaptureActive] = useState(false);

    const setState = useCallback(
        (key, value) => setStates((prev) => ({ ...prev, [key]: value })),
        [setStates]
    );

    // Control methods
    const sendGimbalSpeed = useCallback(
        ({ yaw, pitch }) => send(setGimbalSpeed({ yaw, pitch })),
        [send]
    );

    const sendGimbalAngle = useCallback(
        ({ yaw, pitch }) => send(setGimbalAngle({ yaw, pitch })),
        [send]
    );

    const sendGimbalZoom = useCallback((value) => send(setGimbalZoom(value)), [send]);

    const sendGimbalAbsoluteZoom = useCallback(
        (zoomMultiple) => send(setGimbalAbsoluteZoom(zoomMultiple)),
        [send]
    );

    const sendGimbalFocus = useCallback((value) => send(setGimbalFocus(value)), [send]);

    const sendGimbalTouchFocus = useCallback(
        ({ x, y }) => send(setGimbalTouchFocus({ x, y })),
        [send]
    );

    // Service methods
    const sendGimbalCenter = useCallback(
        () => callService(...gimbalCenter()),
        [callService]
    );

    const sendGimbalAutoFocus = useCallback(
        () => callService(...gimbalAutoFocus()),
        [callService]
    );

    const sendGimbalCaptureImage = useCallback(
        () => callService(...gimbalCaptureImage()),
        [callService]
    );

    const sendGimbalGetStatus = useCallback(
        () => callService(...gimbalGetStatus()),
        [callService]
    );

    // Enable/disable gimbal control based on connection and feature availability
    useEffect(() => {
        const isEnabled = connectionState === ConnectionState.Connected && hasGimbalControl;
        setEnableGimbalControl(isEnabled);
        setShowGimbalControl(isEnabled);
    }, [connectionState, hasGimbalControl]);

    // Subscribe to gimbal state topics
    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected || !hasGimbalControl) {
            return;
        }

        const cleanups = Object.entries(gimbalStatesInfo).map(([key, value]) =>
            attachRosTopicWithRetry(
                controlSocket,
                value.topic,
                (data) => setState(key, value.transform ? value.transform(data) : data),
                { ...LATE_ROS_TOPIC_OPTIONS }
            )
        );

        return () => cleanups.forEach((cleanup) => cleanup());
    }, [controlSocket, connectionState, hasGimbalControl, setState]);

    return (
        <GimbalControlContext.Provider
            value={{
                enableGimbalControl,
                setEnableGimbalControl,
                showGimbalControl,
                setShowGimbalControl,
                controlGimbal,
                setControlGimbal,
                photoCaptureActive,
                setPhotoCaptureActive,
                ...states,
                setGimbalState: setState,
                sendGimbalSpeed,
                sendGimbalAngle,
                sendGimbalZoom,
                sendGimbalAbsoluteZoom,
                sendGimbalFocus,
                sendGimbalTouchFocus,
                sendGimbalCenter,
                sendGimbalAutoFocus,
                sendGimbalCaptureImage,
                sendGimbalGetStatus,
            }}
        >
            {children}
        </GimbalControlContext.Provider>
    );
};

export default GimbalControlProvider;
